/**
 * shared/voice/copy.ts — every sentence anybody reads, in one file.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * **On this path the copy IS the product; the AudioWorklet is not.** P0 ships this file in full,
 * before any of `client/` exists, and its gate is §3.16: sit with a non-technical person and read
 * every screen below, out loud, in order — the front door, the name question, the permission screen,
 * the DENIED permission screen, the room check, the call question, the volume check, the consent
 * line, every coach line, each prompt, the waiting screen, the payoff, and the save offer. Forty
 * minutes. **It will kill more defects than the whole `test/` directory.** More than two "wait, what
 * does that mean?" moments falsifies Appendix D.6, and the fix is to rewrite this file — which is one
 * file, which is why it is one file.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── The vocabulary rule (§3.2) ──────────────────────────────────────────────────────────────────
 *
 * **"studio", "seed", "take", "candidate", "bake-off", "reveal", "provision", "rev", "session
 * voice", "held-out", "gate", "band" and "score" never reach a screen a family member sees.** The
 * internal names are good and they stay; this file is the only translation layer. She is not doing a
 * session. She is making a voice.
 *
 * That is enforced rather than agreed: `BANNED_ON_FAMILY_PATH` below is checked by
 * `test/no-raw-strings-in-studio.test.ts` against every string reachable from `FAMILY`. The same test
 * asserts that no string literal outside this file reaches the DOM from anything under
 * `client/src/studio/` — because the failure mode is not somebody writing "seed" on a screen, it is
 * somebody writing `"Recording stopped unexpectedly"` inline in a catch block at 1 a.m.
 *
 * ── Why templates and not functions ─────────────────────────────────────────────────────────────
 *
 * Every value here is a plain string with `{name}`-style holes filled by `fill()`. It would be
 * tidier as functions. It is not functions because the gate is *reading this file aloud*: a reviewer
 * scrolling past `` `${count} of ${total} saved` `` reads code, and a reviewer scrolling past
 * `"{n} of {total} saved."` reads the sentence. It also makes the whole surface greppable, flattenable
 * and checkable — `familyStrings()` returns every sentence in the product as a list, which is what
 * the banned-word test and the walkthrough script both need.
 *
 * ── VOX-LAW 16 and VOX-LAW 16b, which are why some things are missing ───────────────────────────
 *
 * **VOX-LAW 16. Nothing the gate computes — no band, no score, no similarity number, no percentage —
 * is ever rendered on the family path.** There is no "quality: 82%" string below and there must never
 * be one. Those live on `/admin`, in `CONSOLE`.
 *
 * **VOX-LAW 16b. No exception, assertion or protocol failure anywhere in `client/src/studio/` may
 * render as an error to the person recording.** There is exactly one string for the whole class —
 * `FAMILY.mishap.keepGoing` — and one designed escape hatch, `FAMILY.deferred`. "Recording stopped
 * unexpectedly", "metrics envelope invalid", "buffer overflow" and "ring overflow abort" are
 * sentences that will be read as *your voice broke the computer*.
 */

// ─── the family path ─────────────────────────────────────────────────────────

/**
 * Everything a family member reads, in the order they read it. Do not reorder these groups: the
 * §3.16 walkthrough reads the file top to bottom, and a group out of order is a screen nobody
 * rehearsed.
 *
 * Placeholders, and there are only five, because a sixth is a sentence that should have been two:
 *   `{name}`     the person whose voice this is, as she typed it
 *   `{helper}`   whoever asked for the voice — usually Karti. Never a pronoun after it.
 *   `{n}`        a count, or the current number in a countdown
 *   `{total}`    the other half of a count
 *   `{seconds}`  a whole number of seconds, counting down. Never a percentage, never a bar.
 */
export const FAMILY = {
  /**
   * §3.1 — one page, one button the size of a hand, and a second card that is not a taxonomy.
   *
   * ── `what`, `forWhom` and `safe` are new, and they overturn one paragraph of §3.1 ──────────────
   *
   * The door shipped as a button, a lead about time, and a sign-in card, on the argument that "the
   * only two questions a person standing here actually has are 'how long will this take' and 'do I
   * need to go somewhere else'." **That argument assumed the reader already knows what the site is,
   * and the reader who matters most does not.** Karti read the shipped page back and it reads as
   * bare; the acceptance test is a stranger and a 68-year-old both understanding, in three seconds,
   * *what this is* and *that it is safe*.
   *
   * So three sentences were added and nothing else was. What is still refused, and each was
   * available:
   *
   *  · **No feature list, no "how it works", no three-step diagram.** §3.1 refuses those and the
   *    refusal stands: a numbered diagram is a thing to study before pressing a button.
   *  · **No second decision before the first button.** `what` sits *above* the button as a subtitle
   *    to it, `lead` and `safe` sit below. Nothing added here is a control.
   *  · **No promise the product cannot keep.** `safe` says exactly what VOX-LAW 22 makes true — a
   *    voice reaches nothing until the subject has said out loud, in her own words, that she is
   *    happy for it — and does not say "you can delete everything at any time", which is a sentence
   *    VOX-LAW 26 makes false on purpose.
   */
  door: {
    title: "Make a voice",
    hero: "Keep the voice your family knows.",
    /** The subtitle to the button. What this is, in one sentence, for somebody who has never heard of it. */
    what: "Say a few short things out loud, and this makes a voice that sounds like you.",
    lead: "About seven minutes, somewhere quiet, and that's all.",
    /** Why anybody would want one. Concrete, because "a voice model" means nothing to the reader. */
    forWhom: "Afterwards your family can have it read a story, a chapter, or a letter, in your voice.",
    /** VOX-LAW 22, in plain words. Not a privacy policy — the one fact that answers the real worry. */
    safe: "Nothing is used anywhere until you say out loud, in your own words, that you are happy for it.",
    primary: "Make a voice",
    noAccount: "About seven minutes · No account needed",
    consentFirst: "Your permission comes first.",
    demoWaveLabel: "A sound-wave preview of the recording steps.",
    stepExampleTitle: "Hear the example",
    stepExampleBody: "Listen once, so you know exactly what to say.",
    stepBeepsTitle: "Wait for three little notes",
    stepBeepsBody: "They give you a calm moment before recording begins.",
    stepSpeakTitle: "Say it in your own way",
    stepSpeakBody: "We save each finished recording as you go.",
    outcome: "A voice for stories, letters, and moments still to come.",
    signInTitle: "Sign in",
    signInLead: "Your voices, and the settings for them.",
  },

  /** §3.1 — the only piece of metadata a family ever types. */
  name: {
    question: "Whose voice is this?",
    placeholder: "Her name, or his name",
    help: "This is the name we'll use for the voice from now on. It's the only thing you have to type.",
    next: "Next",
    greeting: "Lovely. Let's make {name}'s voice.",
  },

  /**
   * §3.3 — the microphone moment, which is where most non-technical sessions actually die and which
   * the draft did not mention at all.
   *
   * The failure, concretely: she presses the button, Chrome's permission bubble appears anchored under
   * the address bar at the top-left, her eyes are at the centre on the phrase, she does not see it.
   * She reads aloud to a page that is not listening, presses again, nothing happens, and ninety
   * seconds later she calls her son. Worse: if she clicks Block, or dismisses the bubble twice —
   * which Chrome treats as a denial — **the page can never ask again and no button in the app can fix
   * it.**
   *
   * **VOX-LAW 17. The microphone is requested exactly once, from an explicit gesture on the explainer
   * screen, never on page load, and the app does not advance until the promise settles.**
   *
   * ⚠️ **There is deliberately no `granted` string.** §3.3: "Granted. Straight into §3.4. No
   * congratulation." A string that exists gets rendered, and congratulating somebody for pressing
   * Allow is the first of the small condescensions that add up to her feeling tested.
   */
  mic: {
    explainTitle: "In a moment, Chrome will ask if this page can hear you.",
    explainBody: "Say Allow.",
    /** Drawn in the page, at the real position of the real bubble — not a screenshot of a browser. */
    explainArrow: "The question appears up here.",
    explainButton: "I'm ready",
    deniedTitle: "Chrome isn't letting us hear you yet.",
    deniedBody:
      "Click the little camera-and-microphone icon in the address bar at the top of this window, " +
      "choose Microphone, then Allow — and press this button.",
    deniedRetry: "I've done that",
    /**
     * **The second sentence, shown only after a retry that changed nothing.**
     *
     * ⚠️ It exists because the retry button was, observably, a no-op. On a persistent Chrome block
     * `getUserMedia` rejects in a fraction of a frame, so pressing *"I've done that"* re-rendered a
     * character-for-character identical screen: she presses it, nothing happens, she presses it
     * again, nothing happens, and she concludes the button is broken — on the one screen in the
     * product that has no other way forward.
     *
     * It names Chrome as the thing saying no, not her, and it hands the next move to somebody who can
     * make it. §3.3: *"the recovery that matters is identical in every case — the button that tells
     * Karti."* The screen's emphasis moves to that button at the same moment this sentence appears.
     */
    deniedStill: "Chrome is still saying no. Let's get {helper} to look at it.",
    deniedHelp: "Send this to {helper}",
    /**
     * ⚠️ **It used to say *"Sent. {helper} will have a look."* and that was false in the normal case.**
     *
     * The button published an SSE event, and `publishLive` returns immediately when nobody is
     * reading — which nobody is, because the link that lets him read is on the screen she cannot get
     * past. Nothing was sent, nothing was stored, and this was the last sentence she read at the one
     * dead end in the product.
     *
     * `POST /sessions/:id/help` now writes a durable row and hands back the helper's link, and the
     * sentence says what actually happened. It is the same refusal `keep.linkEmailSent` makes —
     * *"Your mail app has it."* rather than *"Sent."* — for the same reason and in the same words'
     * worth of honesty.
     */
    deniedSent: "The link is copied. Send it to {helper} — it shows him this screen.",
    /** Under the copied link, so she can read it down the phone if pasting is beyond the moment. */
    deniedLinkLabel: "Or read this to {helper}:",
  },

  /**
   * §3.4 — the room check, the most load-bearing minute in the product, and the call question that
   * the stated scenario makes unavoidable.
   *
   * Karti is on a video call from another city, most likely on the same laptop, already holding the
   * microphone. Meet, Zoom and WhatsApp Desktop apply OS-level AEC, AGC and noise suppression
   * *upstream* of Chrome's tab, so VOX-LAW 2's instrumentation would faithfully record
   * `noiseSuppression: false` while the conferencing stack has already gated, ducked and de-reverbed
   * the signal — **a pristine record of destroyed audio, with no error anywhere.** And the playback
   * echoes back into the call.
   *
   * All coaching in the entire product is front-loaded here, before she has performed anything, and
   * phrased about the room rather than about her.
   */
  room: {
    callQuestion: "Is {helper} on a call on this laptop?",
    callYes: "Yes, on this one",
    callNo: "No",
    callMove: "Could {helper} move to a phone first? This laptop needs its ears free.",
    callMoved: "Done — carry on",

    listenTitle: "Let's make sure we can hear you.",
    listenBody: "Say anything at all for three seconds. Your name is perfect.",
    listening: "Listening.",

    /** Job 4. An 880 Hz tone at system volume 10 under a ceiling fan is not a go-cue at all. */
    chimeAsk: "Press play. Did you hear the three little notes?",
    chimePlay: "Play",
    chimeYes: "Yes, I heard it",
    chimeNo: "No, nothing",
    chimeFix: "Turn the volume up with the keys along the top of the keyboard, then press play again.",

    /** Job 2 and job 3, said about the room. Never about her. */
    noisy: "There's a bit of noise in the room. Could someone switch the fan off?",
    tooFar: "Come a hand's width closer.",
    tooClose: "Sit back just a little.",
    echoey: "It's a touch echoey in here. Somewhere with a rug or curtains would be even better.",

    /**
     * VOX-LAW 2b, said once, out of the coach budget, and only when EVERY recording in the session is
     * processed. It is honest about the consequence and does not ask her to fix it, because she
     * cannot — this is a property of the browser she is holding.
     */
    dspProcessed: "This browser is cleaning up the sound — it'll work, but Chrome on a laptop will sound closer.",

    ready: "That sounds lovely. Let's begin.",
  },

  /**
   * §3.6 — the chime is the primary go-cue and the countdown is its visual support. Deviation 4 was
   * REVERSED: her visual channel is fully saturated by the reading task, which is that screen's whole
   * job, and the auditory channel is free.
   */
  cue: {
    listenFirst: "Listen first.",
    playExample: "Play the example",
    sayAgain: "Say it again",
    goHint: "After the three notes, start reading.",
    /** Honest text-only state while Karti's real recording for this line is not on disk. */
    readFromScreen: "For this one, read the words on the screen.",
    /** The countdown. Visual support only — the audio clock is what actually cues her. */
    counting: "{n}",
    go: "Now.",
    recording: "Go ahead.",
    /**
     * The manual ending, and it is the escape hatch for the room this product is actually in: a
     * ceiling fan or a television can hold the level above the silence threshold for as long as it
     * likes, and the automatic ending would then wait for the ceiling.
     *
     * ⚠️ It used to borrow `room.callMoved` — *"Done — carry on"* — which was written for the screen
     * that asks somebody to move a video call to a phone, and which on the recording screen reads as
     * an instruction *to her* rather than as a report *from her*. This is the sentence in her own
     * mouth: she is telling the app she has finished, which is the only thing that button means.
     */
    finished: "I've finished",
    /**
     * **The control that makes sure no phase of the recording screen is ever buttonless.**
     *
     * ⚠️ It exists because of a measured lock-up, not because a screen looked bare. In a *quiet* room
     * — the room the front door tells her to find — the over-cue detector fired on room tone, the
     * prompt restarted, and the loop ran for as long as anyone watched it: countdown, "Now.",
     * restart, every 1.4 s, with a flat meter and *"Let's do that one again — I jumped in too
     * early"* on screen accusing her of talking over a chime she had not heard. 100 seconds, no
     * enabled button anywhere, because `finished` above is rendered only while the region is open and
     * the region was exactly what never opened.
     *
     * So this is rendered during the cue as well, and it is the same escape `finished` is on the
     * other side: **her gesture opens the recording**. Not "skip", not "try again" — both of those
     * are the app telling her something failed. VOX-LAW 15's shape: the way out is forward.
     */
    startNow: "Start when you're ready",
  },

  /**
   * §3.7 — the framing above each prompt.
   *
   * ⚠️ **`scripted` and the held-out prompt share this exact string, and that is deliberate.** If she
   * could tell which recording was being kept out of her voice, she would read it differently, and a
   * reference recorded in a different register is not a reference. Do not add a distinguishing word.
   */
  prompt: {
    consentFrame: "One short sentence to start with. It's your permission, in your own words.",
    unscriptedFrame: "Nothing to read this time — just talk. Any language you like.",
    scriptedFrame: "Read it the way you'd say it to someone sitting with you.",
    ofTotal: "{n} of {total}",
  },

  /**
   * §3.9 — the coach never scores her.
   *
   * **VOX-LAW 15. The gate labels, coaches and chooses. It never refuses to produce a voice. Every
   * failure path ends in a seed.** Once recording starts the app is capped at **two** corrective
   * utterances for the entire session, ever (`COACH_BUDGET` in policy.ts); when the budget is spent,
   * corrections are logged for `/admin` and not shown. Every other post-recording line is
   * unconditionally warm.
   *
   * ⚠️ **`hearItBack` is opt-in and is never automatic.** Everybody hates their own recorded voice —
   * bone conduction guarantees it — and making that the routine per-prompt feedback loop is ten small
   * humiliations in a row, in front of her son.
   */
  coach: {
    gotIt: "Got it.",
    lovely: "Lovely. Next one.",
    thatsIt: "That's the one.",
    perfect: "Perfect.",
    hearItBack: "Hear it back",
    again: "Say it again",
    /** Correction 1 of 2. About the room. */
    correctionNoise: "Something's started up in the room. Could someone switch it off? Let's do that one once more.",
    /** Correction 2 of 2. An instruction, not a judgement. */
    correctionLevel: "Come a hand's width closer, and we'll do that one once more.",
  },

  /**
   * §3.11 — the catalogue of "she did it wrong", none of which is her fault and none of which says so.
   *
   * `overCue` is the only case where enthusiasm costs her anything, and the app takes the blame in the
   * first person. `keepGoing` is VOX-LAW 16b's entire error surface: every exception, assertion and
   * protocol failure in the studio degrades to this one sentence and reports itself to Karti with the
   * session id.
   */
  mishap: {
    overCue: "Let's do that one again — I jumped in too early.",
    /** She closed the laptop lid. Promise only the encoder-finished takes IndexedDB actually has. */
    welcomeBack: "Welcome back — everything you finished is here. Ready to carry on?",
    resumeBody: "We'll carry on from the one you were doing.",
    resumeContinue: "Continue",
    keepGoing: "We've got that one — keep going.",
    nothingHeard: "I didn't quite catch that one. Once more?",
    /**
     * The one control under `keepGoing` when there is nothing else to offer — the family error
     * boundary before a session exists, and the front door after one has ended.
     *
     * It is not *"Next"*. `FAMILY.name.next` is the word on a button that moves forward through a
     * session, and putting it under a sentence that means "something went wrong" tells somebody who
     * has just lost a screen that she is being advanced rather than restarted. **There is
     * deliberately no "Try again" either**: after a session exists, the boundary does not offer a
     * restart at all (`Studio.tsx` explains why — a person who has just recorded four things being
     * asked for a name again is a person who concludes she has lost them).
     */
    startAgain: "Start again",
  },

  /** §3.12 — small, quiet, and never a warning. It is an honest count, not a progress bar. */
  progress: {
    saved: "{n} of {total} saved.",
  },

  /**
   * §3.12 — the wait.
   *
   * The draft had no design for the interval between the last prompt and the payoff, and the measured
   * numbers say it is minutes. **A 68-year-old staring at a spinner for three minutes immediately
   * after doing something exposing concludes it is broken and that it is her fault.** So: a countdown
   * in seconds, and something of her own to look at — her own waveforms, and the things she said. Not
   * a spinner, and not a progress bar that lies.
   */
  wait: {
    title: "Making {name}'s voice.",
    seconds: "About {seconds} seconds.",
    almost: "Almost there.",
    body: "Here's what you said.",
  },

  /**
   * §3.13 — the payoff, and the sentence is authored by the other person.
   *
   * *"Karti, I'm proud of you"*, in his mother's voice, while he is on the call, is the entire product
   * in eight seconds. It costs one text input and one column. If this instead played a line from the
   * script or a neutral test sentence, the moment would be a technology demo — her voice reading a
   * stranger's words about a river — which is uncanny rather than moving, and which invites her to
   * evaluate fidelity, the exact comparison a first clone loses.
   *
   * The words are on screen as they play, so she is never surprised by speech attributed to her, and
   * `delete` actually deletes, immediately, the audio and the row.
   */
  payoff: {
    done: "That's everything. Thank you.",
    intro: "{helper} wrote this for you.",
    /**
     * Said once, above the words, before a sound comes out of the laptop. §3.13: the words are on
     * screen as it plays "so she is never surprised by speech attributed to her" — and a person who
     * reads a sentence with no warning that a machine is about to say it in her own voice has been
     * surprised by the *sound* even though the words were there. This is the warning.
     */
    listen: "In a moment you'll hear it, in your voice.",
    playAgain: "Play it again",
    /** The browser refused to start audio without a gesture. One word, and it is the gesture. */
    play: "Play it",
    delete: "No — delete that",
    deleted: "Gone. Nothing kept.",

    /**
     * ── The "it sounds wrong" path (§3.13), and why it is three sentences rather than a button ───
     *
     * The brief's own pass/fail test is *"if the first thing she hears is a mediocre clone of
     * herself, the product has failed"*, and the honest reading of that is that it **will** happen
     * to somebody, on some evening, on ten seconds of a laptop microphone. What that person must not
     * conclude is that she read it wrong.
     *
     * So the path takes the blame in the first person, states the true mechanism in one clause — it
     * had a few seconds of her to go on — and then offers only actions that actually exist today:
     *
     *  · **`delete` really deletes**, immediately, the audio and the row. It is on this panel as
     *    well as beside the player because the moment she is most likely to want it is the moment
     *    after she has said the thing did not sound like her.
     *  · **`leave` is true**: every recording is on the origin, VOX-LAW 26 exempts an unclaimed
     *    session from every sweep, and the clone lane can be run again from the same recordings
     *    without her saying another word.
     *
     * ⚠️ **There is no "try again" button, and that is a refusal rather than a gap.** Nothing on the
     * family path can re-run the clone: the lane is Karti's, on a serialized GPU, behind an
     * admission token (VOX-LAW 30). A button that appears to re-make her voice and instead enqueues
     * nothing is the one thing worse on this screen than the mediocre clone itself.
     *
     * ⚠️ **And nothing here plays her own recording back at her to compare against** (§3.10, rated
     * fatal). The comparison she is being spared is exactly the one a "here is what you actually
     * sound like" control would force.
     */
    notRight: "That doesn't sound like me",
    notRightTitle: "That one's on us, not on you.",
    notRightBody:
      "It only had a few seconds of you to go on. Everything you said is safe, and {helper} can " +
      "make it again from that — you don't have to say any of it twice.",
    leave: "Leave it for {helper}",
  },

  /**
   * §2.4 and §3.13 — accounts and the durable link, asked about only AFTER she has heard the voice,
   * when there is finally something worth keeping.
   *
   * **VOX-LAW 20. The cookie is never the durable handle to a session — a claim token is.** A cleared
   * cookie, a private window, a handed-back phone, a different browser: the recording is still
   * reachable. `retention` is stated once, in plain words, without a number that will rot.
   */
  keep: {
    offerTitle: "Would you like to keep this?",
    offerBody: "Make an account and this voice is yours.",
    offerYes: "Keep it",
    offerLater: "Not now",

    linkTitle: "This is your link.",
    linkBody: "Keep it somewhere safe. It brings you back to your voice from any computer, at any time.",
    linkCopy: "Copy",
    linkCopied: "Copied.",
    linkEmailLabel: "Or send it to yourself",
    linkEmailButton: "Send",
    /**
     * ⚠️ **Not "Sent."** — because nothing has been sent. There is no mail transport anywhere in
     * this product (`client/src/lib/claimLink.ts` has the full reason), so the button opens her own
     * mail client with the link already in the message. *"Sent."* was a sentence the screen could
     * not know to be true, on the last screen of the session, about the only durable handle to a
     * recording of somebody's mother. This one is what actually happened, and she can see it happen.
     */
    linkEmailSent: "Your mail app has it.",
    retention: "This stays here until someone saves it. Nothing deletes it.",

    longerOffer: "Want to do a few more, so it gets even closer?",
    longerYes: "Yes, a few more",
    longerNo: "That's enough for today",

    /**
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * WHAT "Keep it" LANDS ON. IT USED TO LAND ON THE ENGINEERING CONSOLE.
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * The last thing she is offered is *"Make an account and this voice is yours"*, and after signing
     * up she was dropped on `/voices`, which read — in 11–12 px letter-spaced capitals —
     * `amma@vox · READ · UNCLAIMED · CONSENT PENDING — ASR HAS NOT RUN · no rev`, and then on
     * `/voices/:id`: `AMMA@VOX`, `owner usr_4dl7lxwo9o1ks6lrc550qz66t`,
     * `session ses_89dnyl98n1z86e1j07g230fps`, *"consent WER  not measured (max 0.35)"*, and
     * *"Retuning, previews and the bake-off spend spark-1 and ship with it."* **Zero buttons** —
     * including any way to claim the thing labelled UNCLAIMED.
     *
     * Four words there are forbidden on a family screen by §3.2 and VOX-LAW 16 — *rev*, *bake-off*,
     * *score*, *band* — and a raw WER against a threshold is exactly the "invitation to be judged by
     * a number" VOX-LAW 16 exists to prevent, printed for the person the number is about.
     *
     * So `Keep it` has its own ending now. **It says what happened, in her words, and names what
     * happens next.** `/voices` and `/voices/:id` stay exactly as they are — they are Karti's console
     * and they are good at that — behind a link she is never handed.
     *
     * The claim link is repeated underneath, because §2.4's promise does not stop being true when she
     * signs in: the account is one way back, and the link is the one that works from any browser on
     * any machine with no password at all.
     */
    savedTitle: "{name}'s voice is yours now.",
    savedBody:
      "It is saved to your account. You can come back to it any time — nothing here expires and " +
      "nothing gets deleted.",
    /** Named next steps, so "what happens now" is answered before she has to ask. */
    savedNext: "{helper} can make it say something new whenever you like, and you can always ask for it back.",
    savedHome: "Done",
  },

  /**
   * §3.14 — the deferred path is the COMMON path, not the failure path.
   *
   * Ground truth: spark-1 has no systemd unit **on purpose**, is recovered by a human typing `kuda
   * scene activate 115`, shares one GPU with a 40 GB vLLM, and is stopped by a watchdog when
   * `MemAvailable` falls. "The GPU is unavailable at the end of a twelve-minute session" is not an
   * edge case; it is a scheduled event.
   *
   * **Discovering this at the end is the failure; choosing it at the start is not.** `/health` is
   * probed before prompt 1 and `upFront` is offered as a deliberate choice with two real answers.
   *
   * What she must never learn is that her voice was not good enough, or that she broke it. **The
   * embarrassment is the churn mechanism, and there is no re-engagement email for your mother.**
   *
   * ── "Asleep" was a euphemism, and it was the wrong kind ─────────────────────────────────────────
   *
   * The first draft of this group said *"The computer that makes the voice is asleep right now."*
   * That is soft, and it is **not what is true**, which makes it the one kind of softness this
   * product cannot afford: a computer that is asleep wakes up by itself, so a person told that
   * sentence reasonably expects to wait a few minutes and try again. What is actually true is that
   * the machine is not running, that no timer starts it, and that a person — Karti — starts it by
   * hand. Stating that is *more* reassuring rather than less, because it names somebody who is
   * responsible instead of leaving her to wonder whether the site is broken.
   *
   * It also survives contact with the room: her son is on the video call, he knows perfectly well
   * that the GPU is his, and a screen telling his mother a comforting fiction in front of him costs
   * the product the only thing it has.
   *
   * ⚠️ **`after` does not say "tonight" any more.** spark-1 has no systemd unit on purpose, shares
   * a GPU with a 40 GB vLLM, and is stopped by a watchdog when `MemAvailable` falls. "Tonight" is a
   * promise made on this screen and kept or broken by a box in another building. The sentence that
   * is true on every evening is the one that names the condition instead of the hour.
   *
   * `upFront` is split in two because it is rendered as a heading and a paragraph, and a
   * three-clause sentence set at 32 px is a wall rather than a question.
   */
  deferred: {
    upFrontTitle: "We can record today, and the voice comes a little later.",
    upFrontWhy:
      "The computer that makes voices isn't running just now — {helper} starts it by hand. " +
      "Everything you say today is kept exactly as it is, and {helper} sends you the voice once it " +
      "has run.",
    today: "Record today",
    anotherDay: "Another day",
    after:
      "Your recordings are safe, and nothing about them is lost. {helper} will send you your voice " +
      "once the computer that makes it has run.",

    /* ── The three reasons, because "not running" is only one of them ────────────────────────────
     *
     * `after` above is true in every case and specific in none, and until 2026-08-08 there was only
     * one case for it to be specific about. There are three now, `CompleteResponse.deferredReason`
     * carries which, and `deferredReasonCopy()` at the bottom of this file is the map.
     *
     * The reason this is worth three strings rather than one: **`upFrontWhy` and `after` both assert
     * that the machine is not running, and on the commonest of the three that is false.** spark-1 was
     * verified up on 2026-08-08 with `model_loaded: true`. A session can still defer because the
     * queue in front of it is longer than the 75-second countdown can absorb (`queued`), or because
     * this deployment has nothing configured to do the work at all (`not-configured`). Telling
     * somebody the computer is not running while it is running, one sentence before promising that
     * her voice is coming, spends the credibility of the promise on a detail nobody needed.
     *
     * All three end on the same commitment and none of them mentions a fault, because there is none
     * — §3.14 makes this the common path, not the failure path.
     */

    /** `queued`. Everything works; she is in a line. The commonest case once more than one family
     *  is recording, and the one the single sentence could not express at all. */
    afterQueued:
      "Your recordings are safe. There are a few other voices being made just now, so yours is in " +
      "the line — {helper} will send it to you when it's done.",

    /** `unavailable`. The machine did not answer, or is up without the model loaded. This is the
     *  case the original sentence was written for, and it keeps its honesty about who starts it. */
    afterUnavailable:
      // ⚠️ No pronoun after `{helper}` — this file's own placeholder rule, and it is not stylistic:
      // `VOX_HELPER_NAME` is a deployment setting and the helper is as likely to be a granddaughter
      // as a son. "he'll" was here and would have mis-gendered the person on the call.
      "Your recordings are safe. The computer that makes voices isn't running just now — {helper} " +
      "starts it by hand, and will send you your voice once it has run.",

    /** `not-configured`. Nothing here is set up to do it yet. Said as a fact about the setup, never
     *  as a fact about her recordings, which are complete and perfectly fine. */
    afterNotConfigured:
      "Your recordings are safe. The part that makes voices isn't switched on here yet. {helper} " +
      "will send you your voice as soon as it is.",
  },

  /**
   * §3.15 — the helper's view, offered as a link she can be told to send.
   *
   * Every failure in this product degrades into the same phone call — *"What does it say on the
   * screen?"* — asked by someone who cannot see the screen, answered by someone who does not know
   * which words matter. This converts the entire failure surface from support calls into fixable
   * moments.
   */
  helper: {
    offer: "Send this to {helper}. It shows what you're seeing.",
    copyLink: "Copy the link",
    copied: "Copied.",
  },

  /** `GET /c/:claimToken` — coming back, possibly weeks later, possibly on a different computer. */
  back: {
    title: "Welcome back.",
    body: "Here is the voice you made.",
    listen: "Listen",
    notReady: "It isn't ready yet. {helper} will send it to you the moment it is.",
    /**
     * The same honesty as `deferred.upFrontWhy`, one screen later and weeks apart. Somebody opening
     * a link she kept, on a different computer, finding "not ready yet" and nothing else has no way
     * to tell a system that is working slowly from a link that has stopped working. One clause about
     * *why* is the difference, and it is the same true clause.
     */
    notReadyWhy: "The computer that makes voices runs when {helper} starts it. Your recordings are kept until then.",
  },
} as const;

// ─── Karti's console ─────────────────────────────────────────────────────────

/**
 * `/live`, `/admin`, `/status`. **Not** subject to the vocabulary rule — the internal names are good
 * and they are the right words here. Kept in this file anyway, because a second place for strings is
 * how the first place stops being the only place.
 */
export const CONSOLE = {
  /** §3.15. One writer, one reader, no clock — strictly simpler than movies' authoritative-clock sync. */
  live: {
    title: "Live: {name}",
    prompt: "Prompt",
    level: "Level",
    coachLines: "Coach lines fired",
    coachBudget: "{n} of {total} corrections used",
    uploads: "Takes drained",
    health: "spark-1 /health at session start",
    lastError: "Last error, in full",
    noError: "No errors.",
    ended: "Session ended.",

    /**
     * The empty states, which this page needs more than most: on a healthy session Karti opens it
     * *before* she has done anything, so the first thing he ever sees is every panel empty. An em
     * dash in every slot reads as "this page is broken" at the exact moment he is deciding whether
     * to trust it enough to stop asking her what the screen says.
     */
    connecting: "Connecting to the session…",
    watching: "Connected. Nothing has happened yet.",
    lost: "The connection dropped. The browser is retrying by itself.",
    noCoach: "No coach lines yet.",
    noPrompt: "Not on a prompt yet.",
    noLevel: "No audio arriving yet.",
    noHealth: "No /health was stored for this session.",
    events: "Everything, in order",
    noEvents: "No events yet.",
    copyHoles: "Unfilled copy (a defect — the template and the missing key)",

    /**
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * §3.13's ONE INPUT, and it is on this screen because this is the screen she never sees.
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * *"The reveal sentence is authored by the other person. Before she starts, on the same laptop or
     * from his own, Karti types one sentence he wants to hear in his mother's voice. She never sees
     * it."*
     *
     * ⚠️ Until this field existed the column was settable **only** on `POST /api/v1/sessions`, and no
     * screen ever sent it — `App.tsx` deliberately does not pass it to the studio, because the studio
     * is hers. So every session driven through the product reached the end of the chain with an empty
     * sentence and took the deferred path with a finished clone behind it. The payoff machinery had
     * shipped and its single input had no door.
     *
     * These are `CONSOLE` strings, so they may speak plainly — but the *placeholder* is the design
     * work here, not the label. §3.13's own example is eight words long and is the whole product:
     * a short, specific, personal sentence beats a well-composed one.
     */
    revealTitle: "The sentence she will hear, in her own voice",
    revealHelp:
      "She never sees this. It plays at the end, with the words on screen as they play. Short and " +
      "specific beats well-composed — this is the moment, not a demo.",
    revealPlaceholder: "Karti, I'm proud of you.",
    revealSave: "Save it",
    revealSaved: "Saved. It will play when the voice is ready.",
    revealSpeaking: "Saved — making it now. It appears on her screen when it lands.",
    revealAlready: "The payoff has already played. This is kept as the record of what was asked for.",
    revealNoRev: "Saved. There is no voice on a host for this session yet, so nothing can speak it.",
    /** Said plainly, because the alternative is a helper who assumes the box was empty by accident. */
    revealEmpty: "No sentence yet. Without one this session ends on the deferred path (§3.13).",
  },

  /**
   * `voice_sessions.state`. `permission-denied` is a DISTINCT state and not an indistinguishable
   * "abandoned at step 1" — that distinction is Appendix D.1's detector and the reason §3.3 exists.
   */
  state: {
    new: "Started",
    "permission-denied": "Microphone denied",
    "room-check": "Room check",
    recording: "Recording",
    uploading: "Uploading",
    scoring: "Scoring",
    provisioning: "Provisioning",
    revealed: "Revealed",
    deferred: "Deferred — awaiting spark-1",
    abandoned: "Abandoned",
  },

  /** VOX-LAW 22 and §3.8. `unverified-manual` carries Karti's name on the approval row. */
  consent: {
    pending: "Consent pending — ASR has not run",
    verified: "Consent verified",
    "unverified-manual": "Consent approved by hand",
  },

  /**
   * `/status` — the dependency report. §14, VOX-LAW 28.
   *
   * ⚠️ **This page is mostly red and that is the steady state**, so every label here is written to
   * be read by somebody deciding between two actions rather than by somebody being alarmed: spark-1
   * has no systemd unit *on purpose* and is recovered by a human typing one command. `ttsAsleep`
   * carries that command verbatim, because the whole value of the page is that it ends with a thing
   * to type instead of a thing to worry about.
   *
   * These used to be bare literals in `pages/Status.tsx` with a note saying they would move here
   * "when a `CONSOLE.status` group lands". It has landed.
   */
  status: {
    title: "Status",
    reread: "Read again",
    unreachable: "unreachable",
    notReported: "not reported",
    warnings: "Warnings",
    dependencies: "Dependencies",
    tts: "spark-1 — Chatterbox",
    asr: "spark-1 — ASR",
    origin: "Blob origin — amd-server",
    /** The recovery, verbatim, because a status page whose answer is "go and look" has failed. */
    ttsAsleep: "Unreachable. Run `kuda scene activate 115` on spark-1, then read again.",
    queue: "Queue",
    queueIdle: "0 running · 0 queued (this lane was not reported)",
    disk: "Disk",
    diskApp: "cloud-2 — the app's data directory",
    diskStorage: "amd-server — /mnt/storage",
    raw: "The report, verbatim",
    failed: "This page could not read /api/v1/status. That is a fact about cloud-2, not about spark-1.",

    /**
     * The two gaps that show up nowhere else. See `PayoffReadiness` in `server/src/routes/status.ts`
     * for the derivation and `StatusReport.payoff` for why they are on the contract.
     */
    payoff: "The payoff, and the prompt recordings",
    /** Counted only for sessions that HAVE a revision on a host. A GPU outage must not inflate it. */
    payoffWaiting: "Sessions with a voice and no sentence to speak (§3.13)",
    promptAudio: "Karti's prompt readings on disk (§3.7)",
    promptAudioMissing:
      "the studio shows the text only, which script.ts calls the likeliest reason a first session " +
      "produces a flat seed. docs/recording-the-prompts.md says how.",
  },

  /** §2.4. An unclaimed voice is reported with an age and is exempt from every sweep (VOX-LAW 26). */
  voice: {
    /**
     * ⚠️ **`session` is a LIFECYCLE state, not an ownership one, and this word conflated them.**
     *
     * `voices.status` says where a voice is in its life — `session` means "came out of a recording
     * and has never been published". Whether anybody owns it is `voices.ownerUserId`, and `claim`
     * writes that column and deliberately does not touch `status` (adoption is not publication). So a
     * claimed voice rendered **UNCLAIMED** on the list and on its own detail page, two lines above
     * `owner usr_9481of8ws3a48rhirpr0q436k`. Two different facts wearing one label.
     *
     * The chip is derived from `ownerUserId` now (`pages/Voice.tsx`, `pages/Voices.tsx`), so this
     * string is what a `session` voice with **no owner** says, which is what it always meant.
     */
    session: "Unclaimed",
    /** The same lifecycle state once somebody owns it. Not published; not unowned either. */
    sessionOwned: "Yours — not published",
    provisional: "Provisional — every recording was DSP-processed",
    unverified: "Published, no second opinion",
    published: "Published",
    withdrawn: "Withdrawn",
    unclaimedAge: "Unclaimed for {n} days",
  },
} as const;

// ─── the vocabulary rule, enforced ───────────────────────────────────────────

/**
 * §3.2's thirteen words. Checked case-insensitively on word boundaries against every string reachable
 * from `FAMILY`, by `test/no-raw-strings-in-studio.test.ts`.
 *
 * ⚠️ **"take" is on this list, so the ordinary English verb is banned from family copy too.** "Take
 * your time" is a perfectly good sentence and it is not written above; the rule is worth more than
 * the sentence, because a word-boundary check that has to understand parts of speech is a check
 * nobody trusts and eventually deletes. Write "there is no hurry" instead.
 */
export const BANNED_ON_FAMILY_PATH: readonly string[] = Object.freeze([
  "studio",
  "seed",
  "take",
  "candidate",
  "bake-off",
  "reveal",
  "provision",
  "rev",
  "session voice",
  "held-out",
  "gate",
  "band",
  "score",
]);

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const BANNED_RE = new RegExp(`\\b(?:${BANNED_ON_FAMILY_PATH.map(escapeRe).join("|")})\\b`, "gi");

/** Every banned word in one string, lowercased and deduplicated. Empty means the string is clean. */
export function bannedWordsIn(text: string): string[] {
  const hits = text.match(BANNED_RE);
  return hits ? [...new Set(hits.map((h) => h.toLowerCase()))] : [];
}

/** The five holes `fill()` knows about. A sixth is a sentence that should have been two. */
export const PLACEHOLDERS = ["name", "helper", "n", "total", "seconds"] as const;
export type Placeholder = (typeof PLACEHOLDERS)[number];

const PLACEHOLDER_RE = /\{([a-zA-Z]+)\}/g;

export class CopyError extends Error {
  override name = "CopyError";
}

/**
 * `fill(FAMILY.wait.title, { name: "Ma" })` → `"Making Ma's voice."`
 *
 * **Throws on an unfilled hole.** The alternative — leaving `{name}` on screen — is the one class of
 * copy defect that survives every review, because it only appears when the value is missing at
 * runtime, which is exactly the case nobody screenshotted. VOX-LAW 16b then catches the throw in the
 * studio and degrades it to `FAMILY.mishap.keepGoing`, so the cost of being strict here is one warm
 * sentence and a line on `/admin`, and the cost of being lax is `Making {name}'s voice.` in 32 px in
 * front of a 68-year-old.
 */
export function fill(template: string, vars: Partial<Record<Placeholder, string | number>> = {}): string {
  return template.replace(PLACEHOLDER_RE, (_all, key: string) => {
    if (!(PLACEHOLDERS as readonly string[]).includes(key)) {
      throw new CopyError(`copy: unknown placeholder {${key}} — the five are ${PLACEHOLDERS.join(", ")}`);
    }
    const v = vars[key as Placeholder];
    if (v === undefined || v === null || v === "") {
      throw new CopyError(`copy: {${key}} was not supplied for ${JSON.stringify(template.slice(0, 60))}`);
    }
    return String(v);
  });
}

/** Which holes a template needs. Lets a caller assert its own arguments before it renders. */
export function placeholdersIn(template: string): Placeholder[] {
  const out = new Set<Placeholder>();
  for (const m of template.matchAll(PLACEHOLDER_RE)) {
    const key = m[1]!;
    if (!(PLACEHOLDERS as readonly string[]).includes(key)) {
      throw new CopyError(`copy: unknown placeholder {${key}} in ${JSON.stringify(template.slice(0, 60))}`);
    }
    out.add(key as Placeholder);
  }
  return [...out];
}

type CopyTree = { readonly [k: string]: string | CopyTree };

function flatten(tree: CopyTree, path: string, into: Array<{ path: string; text: string }>): void {
  for (const [k, v] of Object.entries(tree)) {
    const at = path === "" ? k : `${path}.${k}`;
    if (typeof v === "string") into.push({ path: at, text: v });
    else flatten(v, at, into);
  }
}

/**
 * Every family-facing sentence in the product, with its path, in declaration order.
 *
 * Two consumers, and they are the two halves of §3.2: `test/no-raw-strings-in-studio.test.ts` runs
 * `bannedWordsIn` over each, and the §3.16 walkthrough prints them in order so somebody can read them
 * to a non-technical person without opening an editor.
 */
export function familyStrings(): Array<{ path: string; text: string }> {
  const out: Array<{ path: string; text: string }> = [];
  flatten(FAMILY as unknown as CopyTree, "", out);
  return out;
}

/** The same, for `/live` and `/admin`. Exempt from the vocabulary rule; listed so nothing hides. */
export function consoleStrings(): Array<{ path: string; text: string }> {
  const out: Array<{ path: string; text: string }> = [];
  flatten(CONSOLE as unknown as CopyTree, "", out);
  return out;
}

/**
 * `CompleteResponse.deferredReason` → the one sentence that is actually true (§3.14).
 *
 * ⚠️ **The default is the general sentence, not the commonest one.** An unrecognised reason — an
 * older client, a newer server, a field that did not survive a proxy — falls back to
 * `FAMILY.deferred.after`, which is true whatever happened. Guessing `afterUnavailable` because it
 * is the usual case would mean that the one time the mapping breaks, the screen asserts the machine
 * is not running while it is, which is the exact defect these three strings exist to remove.
 *
 * It lives here rather than in the component for the reason the whole file exists: the §3.16
 * walkthrough reads this file aloud, and a `switch` in a `.tsx` is a screen nobody rehearsed.
 */
export function deferredReasonCopy(reason: string | null | undefined): string {
  switch (reason) {
    case "queued":
      return FAMILY.deferred.afterQueued;
    case "unavailable":
      return FAMILY.deferred.afterUnavailable;
    case "not-configured":
      return FAMILY.deferred.afterNotConfigured;
    default:
      return FAMILY.deferred.after;
  }
}
